import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["childNodeIds"];
import { immerable } from 'immer';
import GridNode from './GridNode';
import { DEFAULT_BREAKPOINT_NAME, GRID_NODE_TYPES } from './constants';
import { iterateFrom, mergeIntoPath } from './utils';
export default class SectionNode extends GridNode {
  constructor(_ref) {
    let {
        childNodeIds = []
      } = _ref,
      sectionNodeParams = _objectWithoutPropertiesLoose(_ref, _excluded);
    super(sectionNodeParams);
    this[immerable] = true;
    this._childNodeIds = childNodeIds;
  }
  cloneAndChange(sectionNodeParams) {
    const {
      id,
      childNodeIds,
      parentNodeId,
      value,
      treeRef
    } = sectionNodeParams;
    const newNodeParams = {
      id: id !== null && id !== void 0 ? id : this._id,
      type: GRID_NODE_TYPES.SECTION,
      treeRef: treeRef !== null && treeRef !== void 0 ? treeRef : this._treeRef,
      childNodeIds: childNodeIds !== null && childNodeIds !== void 0 ? childNodeIds : this._childNodeIds,
      parentNodeId: parentNodeId !== null && parentNodeId !== void 0 ? parentNodeId : this._parentNodeId,
      value: Object.assign({}, value !== null && value !== void 0 ? value : this._value, {
        id: id !== null && id !== void 0 ? id : this._id
      })
    };
    return newNodeParams.treeRef.createNewSection(newNodeParams);
  }
  serializeNode(options) {
    return Object.assign({}, this.getValue(), {
      child: this.getChildContainer().serializeNode(options)
    });
  }
  exportNode() {
    return {
      id: this.getId(),
      value: this.getValue(),
      parentNodeId: this.getParentNodeId(),
      childNodeIds: [...this.getChildNodeIds()]
    };
  }

  /**
   * Returns true if section has a container and if the section's child container
   * has a child
   * @returns boolean
   */
  hasChild() {
    return !this.isLeaf() && !this.getChildContainer().isLeaf();
  }
  getChildNodes() {
    return this.getChildNodeIds().map(childNodeId => this.getTree().findNode(childNodeId));
  }

  /**
   * @returns The `child` Container of a Section
   */
  getChildContainer() {
    return this.getChildNodes()[0];
  }
  mergeIntoValue(partialValue, path) {
    return this.cloneAndChange({
      value: mergeIntoPath(this._value, partialValue, path)
    });
  }
  mergeIntoStylesByPath(partialStyles, path) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, path)
      })
    });
  }
  mergeIntoStyles(partialStyles) {
    return this.cloneAndChange({
      value: Object.assign({}, this._value, {
        styles: mergeIntoPath(this.getStyles(), partialStyles, ['breakpointStyles'])
      })
    });
  }
  getBreakpointStyles(breakpoint = DEFAULT_BREAKPOINT_NAME) {
    const defaultStyles = Object.assign({}, this.getStyles().breakpointStyles.default);
    if (breakpoint !== DEFAULT_BREAKPOINT_NAME) {
      return Object.assign({}, defaultStyles, this.getStyles().breakpointStyles[breakpoint]);
    }
    return defaultStyles;
  }
  getStyles() {
    return this.getValue().styles;
  }
  getEffectiveSectionStyle(style, breakpoint, breakpointsByInheritance) {
    var _sectionBreakpointSty;
    const sectionBreakpointStyles = this.getStyles().breakpointStyles;
    const styleAtBreakpoint = (_sectionBreakpointSty = sectionBreakpointStyles[breakpoint]) === null || _sectionBreakpointSty === void 0 ? void 0 : _sectionBreakpointSty[style];
    if (styleAtBreakpoint !== undefined) {
      return {
        value: styleAtBreakpoint,
        inheritedBy: breakpoint
      };
    }
    const parentBreakpoint = breakpointsByInheritance[breakpoint];
    if (parentBreakpoint === undefined) {
      var _sectionBreakpointSty2;
      return {
        value: (_sectionBreakpointSty2 = sectionBreakpointStyles[DEFAULT_BREAKPOINT_NAME]) === null || _sectionBreakpointSty2 === void 0 ? void 0 : _sectionBreakpointSty2[style],
        inheritedBy: DEFAULT_BREAKPOINT_NAME
      };
    }
    return this.getEffectiveSectionStyle(style, parentBreakpoint, breakpointsByInheritance);
  }
  getLabel() {
    var _this$getValue$label;
    return (_this$getValue$label = this.getValue().label) !== null && _this$getValue$label !== void 0 ? _this$getValue$label : '';
  }
  getAllDescendantNodeIds() {
    const descendantNodeIds = [];
    iterateFrom(this, {
      includeStartNode: false,
      gridNodeCallback: ({
        gridNode
      }) => {
        descendantNodeIds.push(gridNode.getId());
      }
    });
    return descendantNodeIds;
  }
  isEmpty(breakpoint = DEFAULT_BREAKPOINT_NAME) {
    const child = this.getChildContainer();
    return !child.isPegboard(breakpoint) && !child.hasChildren();
  }
}